import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import AuthInput from "../../../components/Input/AuthInput";
import AddingLightLoader from "../../../components/Loaders/AddingLightLoader";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import PaymentAccountSelector from "../../../components/Selector/PaymentAccountSelector";
import { GetAccountLogsApi } from "../../../ApiRequests";
import { ErrorToast } from "../../../utils/ShowToast";
import { useDispatch, useSelector } from "react-redux";
import { fetchAccounts } from "../../../store/Slices/AccountSlice";
import { getAppBasePath } from "../../../utils/appPaths";
import { BsSearch } from "react-icons/bs";
import { toLocalYmd } from "../../../utils/dateTime";

const fmtRs = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const accountTypeLabel = (type) =>
  Number(type) === 1 ? "Cash" : "Bank";

const AccountTransactionLog = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const base = getAppBasePath();
  const AccountState = useSelector((state) => state.AccountState);

  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    toLocalYmd()
  );

  useEffect(() => {
    dispatch(fetchAccounts());
  }, [dispatch]);

  const accounts = Array.isArray(AccountState.data) ? AccountState.data : [];

  const account = useMemo(
    () => accounts.find((a) => String(a._id) === String(accountId)),
    [accounts, accountId]
  );

  useEffect(() => {
    if (!accountId) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await GetAccountLogsApi(accountId, { startDate, endDate });
        const payload = res?.data?.data?.payload;
        if (!cancelled) {
          setLogs(Array.isArray(payload?.entries) ? payload.entries : []);
          setSummary(payload?.summary || null);
        }
      } catch (e) {
        if (!cancelled) {
          setLogs([]);
          setSummary(null);
          ErrorToast(
            e.response?.data?.error?.msg || "Could not load account log"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, startDate, endDate]);

  const filteredLogs = useMemo(() => {
    if (!searchText.trim()) return logs;
    const q = searchText.trim().toLowerCase();
    return logs.filter(
      (row) =>
        String(row.type_label || "").toLowerCase().includes(q) ||
        String(row.party || "").toLowerCase().includes(q) ||
        String(row.reference || "").toLowerCase().includes(q) ||
        String(row.desc || "").toLowerCase().includes(q) ||
        String(row.n_date || "").includes(q)
    );
  }, [logs, searchText]);

  const handleAccountChange = (acc) => {
    if (acc?._id && String(acc._id) !== String(accountId)) {
      navigate(`${base}/accounts/${acc._id}/log`, { replace: true });
    }
  };

  if (AccountState.loading && !account) {
    return (
      <BodyWrapper>
        <FetchingLoading />
      </BodyWrapper>
    );
  }

  if (!account) {
    return (
      <BodyWrapper>
        <Header title="Account transaction log" desc="Account not found" Back />
        <div className="text-center py-12 text-muted">
          This account could not be found.
        </div>
      </BodyWrapper>
    );
  }

  return (
    <BodyWrapper>
      <Header
        title="Account transaction log"
        desc={`${account.account_name}${
          account.account_no ? ` · #${account.account_no}` : ""
        } · ${accountTypeLabel(account.type)}`}
        Back
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="lg:col-span-1">
          <PaymentAccountSelector
            label="Account"
            selectedAccount={account}
            setSelectedAccount={handleAccountChange}
          />
        </div>
        <div className="lg:col-span-2 flex flex-wrap items-end gap-3">
          <AuthInput
            label="From"
            Type="date"
            Value={startDate}
            setValue={setStartDate}
          />
          <AuthInput
            label="To"
            Type="date"
            Value={endDate}
            setValue={setEndDate}
          />
        </div>
      </div>

      <div className="mb-5 rounded-2xl border border-main/20 bg-brandSoft px-5 py-4 flex flex-wrap justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted font-semibold">
            Current balance
          </p>
          <p className="text-2xl font-extrabold text-main tabular-nums">
            Rs {fmtRs(account.amount)}
          </p>
        </div>
        {summary && !loading && (
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-lg bg-successSoft border border-success/20 px-3 py-2 font-semibold text-success">
              In: Rs {fmtRs(summary.totalIn)}
            </span>
            <span className="rounded-lg bg-dangerSoft border border-danger/20 px-3 py-2 font-semibold text-danger">
              Out: Rs {fmtRs(summary.totalOut)}
            </span>
            <span className="rounded-lg bg-subtle border border-line px-3 py-2 font-semibold text-inkSecondary">
              Opening: Rs {fmtRs(summary.openingBalance)}
            </span>
            <span className="rounded-lg bg-card border border-main/30 px-3 py-2 font-semibold text-main">
              Closing: Rs {fmtRs(summary.closingBalance)}
            </span>
          </div>
        )}
      </div>

      <div className="mb-4 relative">
        <BsSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input
          type="text"
          placeholder="Search type, party, reference, description…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-line bg-card text-ink rounded-lg text-sm outline-none focus:border-main focus:ring-2 focus:ring-main/15"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <AddingLightLoader />
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center text-muted py-16 rounded-xl border border-dashed border-line bg-subtle/60">
          No transactions for this account in the selected period.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-main shadow-soft">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-main text-white">
                <th className="border border-main px-3 py-2.5 text-left font-bold">
                  Date
                </th>
                <th className="border border-main px-3 py-2.5 text-left font-bold">
                  Type
                </th>
                <th className="border border-main px-3 py-2.5 text-left font-bold">
                  Party
                </th>
                <th className="border border-main px-3 py-2.5 text-left font-bold">
                  Ref
                </th>
                <th className="border border-main px-3 py-2.5 text-left font-bold">
                  Description
                </th>
                <th className="border border-main px-3 py-2.5 text-right font-bold">
                  In
                </th>
                <th className="border border-main px-3 py-2.5 text-right font-bold">
                  Out
                </th>
                <th className="border border-main px-3 py-2.5 text-right font-bold">
                  Balance
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((row) => (
                <tr
                  key={row._id}
                  className="font-quicksand hover:bg-subtle/60 transition-colors"
                >
                  <td className="border border-line px-3 py-2 whitespace-nowrap text-inkSecondary">
                    {row.n_date}
                  </td>
                  <td className="border border-line px-3 py-2 whitespace-nowrap text-inkSecondary">
                    {row.type_label}
                  </td>
                  <td className="border border-line px-3 py-2 text-ink">
                    {row.party}
                  </td>
                  <td className="border border-line px-3 py-2 text-inkSecondary">
                    {row.reference}
                  </td>
                  <td className="border border-line px-3 py-2 max-w-[280px] text-inkSecondary">
                    {row.desc}
                  </td>
                  <td className="border border-line px-3 py-2 text-right tabular-nums text-success font-semibold">
                    {row.dr ? fmtRs(row.dr) : "—"}
                  </td>
                  <td className="border border-line px-3 py-2 text-right tabular-nums text-danger font-semibold">
                    {row.cr ? fmtRs(row.cr) : "—"}
                  </td>
                  <td className="border border-line px-3 py-2 text-right tabular-nums font-bold text-ink">
                    {fmtRs(row.bal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </BodyWrapper>
  );
};

export default AccountTransactionLog;
